import { React, useCallback, useEffect, useState } from "react";
import {
    Grid,
    TextField,
    Typography,
    Box,
    Card,
    CardContent,
    Stack,
    IconButton,
    Tooltip,
    FormControl,
    Select,
    MenuItem,
    FormControlLabel,
    FormLabel,
    RadioGroup,
    Radio,
    Chip,
    Checkbox,
    ListItemText,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import EditIcon from "@mui/icons-material/Edit";
import VerifiedIcon from "@mui/icons-material/Verified";

import { verifyDomain } from "../../Actions/OrganizationAction";
import { MyAccountBalanceIcon, MyCreditCardIcon, MyInfoIcon, MyVerifiedIcon } from "../../Assets/svg/MyIcons";
import { CustomSwitch } from "../../Assets/customTheme/customswitch";
import CloseIcon from "@mui/icons-material/Close";

import { listAllKycPlansDropDownAction, listCountriesAction } from "../../Actions/KycPlanManagemantAction";

const baseDomain = "netcluesdemo.com";
const KycInfoStep = ({
    tenantId = null,
    formik,
    countryList,
    tenantCurrencies = [],
    qbotDomain,
    paycentralDomain,
    isTrialPortalCreation
}) => {

    const [isEditing, setIsEditing] = useState(false);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);
    const [buttonColor, setButtonColor] = useState("");
    const [planList, setPlanList] = useState([]);
    const [previousDomain, setPreviousDomain] = useState("");
    const [isManuallyEdited, setIsManuallyEdited] = useState(false);
    const [responseMessage, setResponseMessage] = useState("");

    const generateDefaultDomainUrl = (subdomain) => {
        if (!subdomain) return "";

        const cleanSubdomain = subdomain
            .replace(/\s+/g, "")
            .replace(/[^a-zA-Z0-9]/g, "")
            .toLowerCase();

        return `${cleanSubdomain}.${baseDomain}`;
    };

    const cleanDomain = (value) =>
        value?.trim().toLowerCase().replace(/^https?:\/\//, "");

    const fetchAllPlansDropDownList = useCallback(async () => {
        try {
            const response = await listAllKycPlansDropDownAction();

            const plans = response?.data?.data || [];

            setPlanList(plans);
            const selectedPlan = plans.find(p => p.isSelected);

            if (selectedPlan) {
                formik.setFieldValue("kycPlanId", selectedPlan.id);
                formik.setFieldValue(
                    "kycBillingCycle",
                    selectedPlan.selectedBillingCycle || ""
                );
            }

        } catch (error) {
            console.error("error ->", error);
        }
    }, [formik]);

    useEffect(() => {
        fetchAllPlansDropDownList();
    }, []);

    const handleEditClick = () => {
        setPreviousDomain(formik.values.kycDomainUrl);

        const currentDomain = formik.values.kycDomainUrl;
        const subdomain = currentDomain
            ?.replace(/^https?:\/\//i, "")
            ?.split(".")[0];

        formik.setFieldValue("kycDomainUrl", subdomain || "");
        formik.setFieldValue("kycIsVerified", false);

        setIsEditing(true);
        setButtonColor("");
        setIsButtonDisabled(true);
    };

    const handleSaveDomainUrl = () => {
        let subdomain = formik.values.kycDomainUrl.trim();

        if (!subdomain) {
            formik.setFieldError("kycDomainUrl", "Please enter a subdomain");
            return;
        }

        subdomain = subdomain
            .replace(/^https?:\/\//, "")
            .replace(`.${baseDomain}`, "")
            .split(".")[0];

        const fullDomain = `${subdomain}.${baseDomain}`;
        formik.setFieldValue("kycDomainUrl", fullDomain);
        formik.setFieldValue("kycIsVerified", false);

        setIsManuallyEdited(true);
        setIsEditing(false);
        setButtonColor("");
        setIsButtonDisabled(false);
    };

    useEffect(() => {
        if (!formik.values.kycIsVerified) {
            formik.setFieldError("kycIsVerified", "Please verify your domain");
        }
    }, [formik.values.kycIsVerified]);

    const verifyDomainURL = async () => {
        try {
            const kycDomain = cleanDomain(formik.values.kycDomainUrl);
            const paycentralDomainValue = cleanDomain(paycentralDomain?.values?.domainUrl);
            const qbotDomainValue = cleanDomain(qbotDomain?.values?.domainUrl);

            if (
                kycDomain === paycentralDomainValue ||
                kycDomain === qbotDomainValue
            ) {
                setButtonColor("error");
                setResponseMessage(
                    "KYC domain must be different from QBOT and PayCentral domains."
                );
                formik.setFieldValue("kycIsVerified", false);
                formik.setFieldError("kycIsVerified", "Please verify your domain");
                return;
            }

            let subdomain = formik.values.kycDomainUrl?.trim();

            if (!subdomain) {
                formik.setFieldValue("kycIsVerified", false);
                formik.setFieldError("kycIsVerified", "Please verify your domain");
                setResponseMessage("Please enter a domain");
                setButtonColor("error");
                return;
            }
            subdomain = subdomain
                .replace(/^https?:\/\//, "")
                .replace(`.${baseDomain}`, "")
                .split(".")[0];

            const fullDomain = `${subdomain}.${baseDomain}`;

            const response = await verifyDomain({
                domainUrl: fullDomain,
            });

            if (response?.data?.status === 200) {
                setButtonColor("error");
                setResponseMessage("Domain already exists");
                formik.setFieldValue("kycIsVerified", false);
                formik.setFieldError("kycIsVerified", "Please verify your domain");
                setIsButtonDisabled(false);
            } else if (response?.data?.status === 202) {
                setButtonColor("success");
                setResponseMessage("Domain verified successfully");
                formik.setFieldValue("kycIsVerified", true);
                setIsButtonDisabled(true);

                formik.setFieldValue("kycDomainUrl", fullDomain);
            }

            setTimeout(() => setResponseMessage(""), 3000);
        } catch (error) {
            setButtonColor("error");
            setResponseMessage(
                error?.response?.data?.message || "Verification failed. Try again."
            );
            formik.setFieldValue("kycIsVerified", false);
            formik.setFieldError("kycIsVerified", "Please verify your domain");
            setIsButtonDisabled(false);
        }
    };

    const handleCancelEdit = () => {
        formik.setFieldValue("kycDomainUrl", previousDomain);
        formik.setFieldError("kycDomainUrl", "");
        setIsEditing(false);
    };

    const handleCompanyNameChange = (e) => {
        formik.setFieldValue(e.target.name, e.target.value.replace(/^\s+/, ''));
        formik.setFieldTouched(e.target.name, true, false);
    };

    const handleEmailChange = (e) => {
        formik.setFieldValue(e.target.name, e.target.value.replace(/\s/g, ''));
        formik.setFieldTouched(e.target.name, true, false);
    };

    useEffect(() => {
        if (formik.values.kycIsVerified) {
            setButtonColor("success");
            setIsButtonDisabled(true);
        }
    }, [formik.values.kycIsVerified]);

    useEffect(() => {
        if (formik.values.kycIsVerified) return;
        if (!isEditing && !formik.values.kycIsVerified && !isManuallyEdited && !formik.touched.kycDomainUrl && (!tenantId || isTrialPortalCreation)) {
            const username = formik.values.kycUserName;

            if (username) {
                const cleanValue = username
                    .replace(/\s+/g, "")
                    .replace(/[^a-zA-Z0-9]/g, "")
                    .toLowerCase();

                formik.setFieldValue("kycDomainUrl", `${cleanValue}.${baseDomain}`);
                formik.setFieldValue("kycIsVerified", false);
                formik.setFieldError("kycIsVerified", "Please verify your domain");
                setButtonColor("");
                setIsButtonDisabled(false);
            }
        }
    }, [formik.values.kycUserName, isEditing, formik.values.kycIsVerified, isManuallyEdited]);

    useEffect(() => {
        const kycDomain = cleanDomain(formik.values.kycDomainUrl);
        const paycentralDomainValue = cleanDomain(paycentralDomain?.values?.domainUrl);
        const qbotDomainValue = cleanDomain(qbotDomain?.values?.domainUrl);

        if (!kycDomain) return;

        if (kycDomain === paycentralDomainValue) {
            formik.setFieldError(
                "kycDomainUrl",
                "KYC and PayCentral domains cannot be the same."
            );
            return;
        }

        if (kycDomain === qbotDomainValue) {
            formik.setFieldError(
                "kycDomainUrl",
                "KYC and QBOT domains cannot be the same."
            );
            return;
        }

    }, [
        formik.values.kycDomainUrl,
        formik.values.domainUrl,
        qbotDomain?.values?.domainUrl
    ]);

    return (
        <>
            {/* Form */}
            <Grid container spacing={3}>

                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                mb: 0.8,
                                color: "rgba(0,0,0,0.7)"
                            }}
                        >
                            User Name <span style={{ color: "red" }}>*</span>
                        </Typography>

                        <TextField
                            fullWidth
                            name="kycUserName"
                            value={formik.values.kycUserName}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Enter user name"
                            error={formik.touched.kycUserName && Boolean(formik.errors.kycUserName)}
                            helperText={formik.touched.kycUserName && formik.errors.kycUserName}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    backgroundColor: "#fff",
                                    "& fieldset": {
                                        borderColor: "rgba(0,0,0,0.2)"
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#1976d2"
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#1976d2",
                                        borderWidth: "1.5px"
                                    }
                                },
                                "& .MuiInputBase-input": {
                                    padding: "12px"
                                }
                            }}
                        />
                    </FormControl>
                </Grid>

                {/* COUNTRY */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                mb: 0.8,
                                color: "rgba(0,0,0,0.7)"
                            }}
                        >
                            Country <span style={{ color: "red" }}>*</span>
                        </Typography>

                        <Select
                            multiple
                            name="kycCountry"
                            value={
                                formik.values.kycCountry
                                    ? formik.values.kycCountry.split(",")
                                    : []
                            }
                            onChange={(e) => {
                                let value = e.target.value;
                                if (!Array.isArray(value)) value = [value];
                                formik.setFieldValue("kycCountry", value.join(","));
                            }}
                            onBlur={formik.handleBlur}
                            displayEmpty
                            renderValue={(selected) => {
                                if (!selected.length) {
                                    return <span style={{ color: "#999" }}>Select countries</span>;
                                }

                                return (
                                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                        {selected.map((code) => {
                                            const country = countryList.find(c => c.code === code);
                                            return (
                                                <Chip
                                                    key={code}
                                                    label={country?.name || code}
                                                    size="small"
                                                    sx={{
                                                        borderRadius: "6px",
                                                        background: "rgba(25,118,210,0.1)",
                                                        color: "#1976d2",
                                                        fontWeight: 500
                                                    }}
                                                />
                                            );
                                        })}
                                    </Box>
                                );
                            }}
                            sx={{
                                borderRadius: "10px",
                                backgroundColor: "#fff",
                                minHeight: "48px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(0,0,0,0.2)"
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#1976d2"
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#1976d2",
                                    borderWidth: "1.5px"
                                }
                            }}
                        >
                            {countryList.map((country) => (
                                <MenuItem key={country.id} value={country.code}>
                                    <Checkbox
                                        checked={
                                            formik.values.kycCountry
                                                ?.split(",")
                                                ?.includes(country.code)
                                        }
                                    />
                                    <ListItemText primary={country.name} />
                                </MenuItem>
                            ))}
                        </Select>

                        {formik.touched.kycCountry && formik.errors.kycCountry && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {formik.errors.kycCountry}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                mb: 0.8,
                                color: "rgba(0,0,0,0.7)"
                            }}
                        >
                            Company Name <span style={{ color: "red" }}>*</span>
                        </Typography>

                        <TextField
                            fullWidth
                            name="kycCompanyName"
                            value={formik.values.kycCompanyName}
                            onChange={handleCompanyNameChange}
                            onBlur={(e) => {
                                formik.setFieldValue("kycCompanyName", e.target.value.trim());
                                formik.handleBlur(e);
                            }}
                            placeholder="Enter company name"
                            disabled={Boolean(tenantId) && !isTrialPortalCreation}
                            error={formik.touched.kycCompanyName && Boolean(formik.errors.kycCompanyName)}
                            helperText={formik.touched.kycCompanyName && formik.errors.kycCompanyName}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    backgroundColor: tenantId ? "#f5f5f5" : "#fff",
                                    "& fieldset": {
                                        borderColor: "rgba(0,0,0,0.2)"
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#1976d2"
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#1976d2",
                                        borderWidth: "1.5px"
                                    }
                                },
                                "& .MuiInputBase-input": {
                                    padding: "12px"
                                }
                            }}
                        />
                    </FormControl>
                </Grid>


                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                mb: 0.8,
                                color: "rgba(0,0,0,0.7)"
                            }}
                        >
                            Company Email <span style={{ color: "red" }}>*</span>
                        </Typography>

                        <TextField
                            fullWidth
                            name="kycCompanyEmail"
                            value={formik.values.kycCompanyEmail}
                            onChange={handleEmailChange}
                            onBlur={formik.handleBlur}
                            placeholder="Enter company email"
                            error={formik.touched.kycCompanyEmail && Boolean(formik.errors.kycCompanyEmail)}
                            helperText={formik.touched.kycCompanyEmail && formik.errors.kycCompanyEmail}
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: "10px",
                                    backgroundColor: "#fff",
                                    "& fieldset": {
                                        borderColor: "rgba(0,0,0,0.2)"
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#1976d2"
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#1976d2",
                                        borderWidth: "1.5px"
                                    }
                                },
                                "& .MuiInputBase-input": {
                                    padding: "12px"
                                }
                            }}
                        />
                    </FormControl>
                </Grid>

                {/* DOMAIN */}
                <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                        {/* LABEL */}
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                mb: 0.8,
                                color: "rgba(0,0,0,0.7)"
                            }}
                        >
                            Domain <span style={{ color: "red" }}>*</span>
                        </Typography>

                        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                            {/* INPUT */}
                            <TextField
                                fullWidth
                                name="kycDomainUrl"
                                value={
                                    isEditing
                                        ? formik.values.kycDomainUrl?.split(".")[0] || ""
                                        : formik.values.kycDomainUrl || ""
                                }
                                onChange={(e) => {
                                    if (isEditing) {
                                        const value = e.target.value
                                            .toLowerCase()
                                            .replace(/[^a-z0-9-]/g, "");
                                        formik.setFieldValue("kycDomainUrl", value);
                                    }
                                }}
                                onBlur={formik.handleBlur}
                                placeholder={isEditing ? "Enter subdomain" : ""}
                                disabled={
                                    !isEditing ||
                                    (Boolean(tenantId) && !isTrialPortalCreation && !isTrialPortalCreation)
                                }
                                error={formik.touched.kycDomainUrl && Boolean(formik.errors.kycDomainUrl)}
                                helperText={formik.touched.kycDomainUrl && formik.errors.kycDomainUrl}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "10px",
                                        backgroundColor: !isEditing ? "#f5f5f5" : "#fff",
                                        "& fieldset": {
                                            borderColor: "rgba(0,0,0,0.2)"
                                        },
                                        "&:hover fieldset": {
                                            borderColor: "#1976d2"
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: "#1976d2",
                                            borderWidth: "1.5px"
                                        }
                                    },
                                    "& .MuiInputBase-input": {
                                        padding: "12px"
                                    }
                                }}
                                InputProps={{
                                    endAdornment: isEditing && (
                                        <Typography
                                            sx={{
                                                fontSize: "13px",
                                                color: "#888",
                                                whiteSpace: "nowrap",
                                                ml: 1
                                            }}
                                        >
                                            .{baseDomain}
                                        </Typography>
                                    )
                                }}
                            />

                            {/* ACTION BUTTONS */}
                            <Box sx={{ display: "flex", gap: 0.5 }}>
                                {isEditing ? (
                                    <>
                                        <Tooltip title="Save">
                                            <IconButton
                                                onClick={handleSaveDomainUrl}
                                                sx={{
                                                    border: "1px solid #1976d2",
                                                    color: "#1976d2",
                                                    "&:hover": { background: "rgba(25,118,210,0.1)" }
                                                }}
                                            >
                                                <SaveIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>

                                        <Tooltip title="Cancel">
                                            <IconButton
                                                onClick={handleCancelEdit}
                                                sx={{
                                                    border: "1px solid #d32f2f",
                                                    color: "#d32f2f",
                                                    "&:hover": { background: "rgba(211,47,47,0.1)" }
                                                }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </>
                                ) : (
                                    <>
                                            {(!tenantId || isTrialPortalCreation) && (
                                            <Tooltip title="Edit">
                                                <span>
                                                    <IconButton
                                                        onClick={handleEditClick}
                                                            disabled={!formik.values.kycDomainUrl || (Boolean(tenantId) && !isTrialPortalCreation && !isTrialPortalCreation)}
                                                        sx={{
                                                            border: "1px solid #1976d2",
                                                            color: "#1976d2",
                                                            "&.Mui-disabled": { opacity: 0.5 }
                                                        }}
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        )}

                                        {/* VERIFY BUTTON */}
                                        <Tooltip
                                            title={
                                                formik.values.kycIsVerified
                                                    ? "Verified"
                                                    : "Verify Domain"
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    onClick={verifyDomainURL}
                                                        disabled={
                                                            isButtonDisabled ||
                                                            (Boolean(tenantId) && !isTrialPortalCreation && !isTrialPortalCreation)
                                                        }
                                                    sx={{
                                                        border: `1px solid ${formik.values.kycIsVerified
                                                            ? "#2e7d32"
                                                            : buttonColor === "error"
                                                                ? "#d32f2f"
                                                                : "#1976d2"
                                                            }`,
                                                        color: formik.values.kycIsVerified
                                                            ? "#2e7d32"
                                                            : buttonColor === "error"
                                                                ? "#d32f2f"
                                                                : "#1976d2",
                                                        "&.Mui-disabled": { opacity: 0.5 }
                                                    }}
                                                >
                                                    <MyVerifiedIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </>
                                )}
                            </Box>
                        </Box>

                        {/* ERROR */}
                        {formik.touched.kycIsVerified &&
                            formik.errors.kycIsVerified &&
                            !formik.values.kycIsVerified && (
                                <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                    {formik.errors.kycIsVerified}
                                </Typography>
                            )}

                        {/* RESPONSE */}
                        {responseMessage && (
                            <Typography
                                variant="caption"
                                sx={{
                                    mt: 0.5,
                                    color:
                                        buttonColor === "success"
                                            ? "success.main"
                                            : "error.main"
                                }}
                            >
                                {responseMessage}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>


                {/* AUTO PAY */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                mb: 0.8,
                                color: "rgba(0,0,0,0.7)"
                            }}
                        >
                            Auto Pay
                        </Typography>

                        <Box
                            sx={{
                                border: "1px solid rgba(0,0,0,0.2)",
                                borderRadius: "10px",
                                px: 2,
                                py: 1.5,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                background: "#fff",
                                transition: "0.3s",
                                "&:hover": {
                                    borderColor: "#1976d2"
                                }
                            }}
                        >
                            <Typography sx={{ fontSize: "14px", color: "#555" }}>
                                Enable Auto Pay
                            </Typography>

                            <CustomSwitch
                                name="kycIsAutoPay"
                                checked={formik.values.kycIsAutoPay}
                                onChange={(event) =>
                                    formik.setFieldValue("kycIsAutoPay", event.target.checked)
                                }
                            />
                        </Box>
                    </FormControl>
                </Grid>

                {/* Select Plan*/}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                mb: 0.8,
                                color: "rgba(0,0,0,0.7)"
                            }}
                        >
                            Select Plan <span style={{ color: "red" }}>*</span>
                        </Typography>

                        <Select
                            name="kycPlanId"
                            value={formik.values.kycPlanId || ""}
                            onChange={(e) => {
                                const selectedPlanId = e.target.value;

                                formik.setFieldValue("kycPlanId", selectedPlanId);

                                const selectedPlan = planList.find(p => p.id === selectedPlanId);

                                if (selectedPlan?.selectedBillingCycle) {
                                    formik.setFieldValue("kycBillingCycle", selectedPlan.selectedBillingCycle);
                                } else {
                                    formik.setFieldValue("kycBillingCycle", "");
                                }
                            }}
                            displayEmpty
                            disabled={Boolean(planList.length === 0)}
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <span style={{ color: "#999" }}>Select a plan</span>;
                                }
                                const plan = planList.find((p) => p.id === selected);
                                return plan ? plan.name : "";
                            }}
                            sx={{
                                borderRadius: "10px",
                                backgroundColor: "#fff",
                                minHeight: "48px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(0,0,0,0.2)"
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#1976d2"
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#1976d2",
                                    borderWidth: "1.5px"
                                }
                            }}
                        >
                            <MenuItem value="" disabled>
                                <em>Select a plan</em>
                            </MenuItem>

                            {planList.map((plan) => (
                                <MenuItem key={`plan-${plan.id}`} value={plan.id}>
                                    {plan.name}
                                </MenuItem>
                            ))}
                        </Select>

                        {formik.touched.kycPlanId && formik.errors.kycPlanId && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {formik.errors.kycPlanId}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>


                {/* BILLING CYCLE */}
                <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                        <Typography
                            sx={{
                                fontSize: "13px",
                                fontWeight: 500,
                                mb: 0.8,
                                color: "rgba(0,0,0,0.7)"
                            }}
                        >
                            Billing Cycle <span style={{ color: "red" }}>*</span>
                        </Typography>

                        <Select
                            name="kycBillingCycle"
                            value={formik.values.kycBillingCycle || ""}
                            onChange={formik.handleChange}
                            displayEmpty
                            disabled={!formik.values.kycPlanId}
                            renderValue={(selected) => {
                                if (!selected) {
                                    return <span style={{ color: "#999" }}>Select billing cycle</span>;
                                }
                                return selected;
                            }}
                            sx={{
                                borderRadius: "10px",
                                backgroundColor: !formik.values.kycPlanId ? "#f5f5f5" : "#fff",
                                minHeight: "48px",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "rgba(0,0,0,0.2)"
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#1976d2"
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#1976d2",
                                    borderWidth: "1.5px"
                                }
                            }}
                        >
                            <MenuItem value="" disabled>
                                <em>Select billing cycle</em>
                            </MenuItem>

                            <MenuItem value="monthly">Monthly</MenuItem>
                            <MenuItem value="yearly">Yearly</MenuItem>
                        </Select>

                        {formik.touched.kycBillingCycle && formik.errors.kycBillingCycle && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                                {formik.errors.kycBillingCycle}
                            </Typography>
                        )}
                    </FormControl>
                </Grid>

                {/* Payment Mode */}
                <Grid item xs={12} sm={6}>
                    <Box>
                        <Typography
                            sx={{
                                fontSize: "14px",
                                fontWeight: 500,
                                mb: 1,
                                color: "rgba(0,0,0,0.7)"
                            }}
                        >
                            Payment Mode <span style={{ color: "red" }}>*</span>
                        </Typography>

                        <Grid container spacing={2}>
                            {/* ONLINE PAYMENT */}
                            <Grid item xs={6}>
                                <Box
                                    onClick={() => formik.setFieldValue("kycPaymentMode", "online")}
                                    sx={{
                                        border: formik.values.kycPaymentMode === "online"
                                            ? "2px solid #1976d2"
                                            : "1px solid rgba(0,0,0,0.2)",
                                        borderRadius: "12px",
                                        padding: "14px",
                                        cursor: "pointer",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        height: "90px",
                                        transition: "all 0.3s ease",
                                        background:
                                            formik.values.kycPaymentMode === "online"
                                                ? "rgba(25,118,210,0.08)"
                                                : "#fff",
                                        "&:hover": {
                                            borderColor: "#1976d2",
                                            background: "rgba(25,118,210,0.05)"
                                        }
                                    }}
                                >
                                    <MyCreditCardIcon
                                        sx={{
                                            fontSize: 28,
                                            color:
                                                formik.values.kycPaymentMode === "online"
                                                    ? "#1976d2"
                                                    : "gray",
                                            mb: 0.5
                                        }}
                                    />
                                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                                        Online Payment
                                    </Typography>
                                </Box>
                            </Grid>

                            {/* OFFLINE PAYMENT */}
                            <Grid item xs={6}>
                                <Box
                                    onClick={() => formik.setFieldValue("kycPaymentMode", "offline")}
                                    sx={{
                                        border: formik.values.kycPaymentMode === "offline"
                                            ? "2px solid #2e7d32"
                                            : "1px solid rgba(0,0,0,0.2)",
                                        borderRadius: "12px",
                                        padding: "14px",
                                        cursor: "pointer",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        height: "90px",
                                        transition: "all 0.3s ease",
                                        background:
                                            formik.values.kycPaymentMode === "offline"
                                                ? "rgba(46,125,50,0.08)"
                                                : "#fff",
                                        "&:hover": {
                                            borderColor: "#2e7d32",
                                            background: "rgba(46,125,50,0.05)"
                                        }
                                    }}
                                >
                                    <MyAccountBalanceIcon
                                        sx={{
                                            fontSize: 28,
                                            color:
                                                formik.values.kycPaymentMode === "offline"
                                                    ? "#2e7d32"
                                                    : "gray",
                                            mb: 0.5
                                        }}
                                    />
                                    <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                                        Offline Payment
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* ERROR */}
                        {formik.touched.kycPaymentMode && formik.errors.kycPaymentMode && (
                            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                {formik.errors.kycPaymentMode}
                            </Typography>
                        )}
                    </Box>
                </Grid>


                {/* CURRENCY */}
                <Grid item xs={12} md={6}>
                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            mb: 1,
                            color: "#1f2937",
                        }}
                    >Currency <span className="text-danger ms-1">*</span></Typography>
                    <TextField sx={{
                        "& .MuiOutlinedInput-root": {
                            height: "42px",
                            borderRadius: "12px",
                        },
                    }} select name="kycCurrency" size="small" placeholder="Select Currency" value={formik.values.kycCurrency} onChange={formik.handleChange} onBlur={formik.handleBlur} error={formik.touched.kycCurrency && Boolean(formik.errors.kycCurrency)} helperText={formik.touched.kycCurrency && formik.errors.kycCurrency} fullWidth>
                        {tenantCurrencies.map((kycCurrency) => (<MenuItem key={kycCurrency.id} value={kycCurrency.id}>{kycCurrency?.name || ""} - {kycCurrency?.symbol || ""}</MenuItem>))}
                    </TextField>
                </Grid>

            </Grid>
        </>

    );
};

export default KycInfoStep;